import { Check, Clock, AlertCircle, Info, X, DollarSign } from 'lucide-react';
import { Badge, Icon, Text } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const variants = ['default', 'success', 'warning', 'error', 'info', 'primary'] as const;
const sizes = ['sm', 'md'] as const;

function BadgeShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="badge">
          <Badge
            variant={
              playgroundProps.variant as
                | 'default'
                | 'success'
                | 'warning'
                | 'error'
                | 'info'
                | 'primary'
            }
            size={playgroundProps.size as 'sm' | 'md'}
            dot={playgroundProps.dot as boolean}
            pill={playgroundProps.pill as boolean}
          >
            Active
          </Badge>
      </ShowcasePreviewSection>

      {/* Variant Examples */}
      <ShowcaseDemoSection
        title="Variants"
        description="Six color variants for different semantic meanings"
       
      >
          <HStack gap={4} wrap="wrap">
            {variants.map((variant) => (
              <Badge key={variant} variant={variant}>
                {variant}
              </Badge>
            ))}
          </HStack>
      </ShowcaseDemoSection>

      {/* Size Variants */}
      <ShowcaseDemoSection title="Sizes" description="Two sizes available: small (18px) and medium (22px)">
          <VStack gap={3}>
            {sizes.map((size) => (
              <HStack key={size} gap={4} align="center">
                <Text size="sm" color="muted" className="w-16">
                  {size}
                </Text>
                <HStack gap={2}>
                  <Badge size={size} variant="primary">
                    Primary
                  </Badge>
                  <Badge size={size} variant="success">
                    Success
                  </Badge>
                  <Badge size={size} variant="warning">
                    Warning
                  </Badge>
                </HStack>
              </HStack>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Dot Indicator */}
      <ShowcaseDemoSection
        title="With Dot Indicator"
        description="Add a colored dot for emphasis on status badges"
       
      >
          <HStack gap={4} wrap="wrap">
            <Badge variant="success" dot>
              Active
            </Badge>
            <Badge variant="warning" dot>
              Pending
            </Badge>
            <Badge variant="error" dot>
              Expired
            </Badge>
            <Badge variant="info" dot>
              New
            </Badge>
            <Badge variant="default" dot>
              Draft
            </Badge>
          </HStack>
      </ShowcaseDemoSection>

      {/* Pill Style */}
      <ShowcaseDemoSection
        title="Pill Style"
        description="Full border-radius for a softer appearance"
       
      >
          <HStack gap={4} wrap="wrap">
            <Badge variant="primary" pill>
              Featured
            </Badge>
            <Badge variant="success" pill>
              Approved
            </Badge>
            <Badge variant="warning" pill>
              Review
            </Badge>
            <Badge variant="error" pill>
              Rejected
            </Badge>
            <Badge variant="info" pill>
              Updated
            </Badge>
          </HStack>
      </ShowcaseDemoSection>

      {/* Insurance Domain Examples */}
      <ShowcaseDemoSection
        title="Insurance Domain Examples"
        description="Real-world usage in insurance applications"
       
      >
          <VStack gap={4}>
            {/* Policy Statuses */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                Policy Statuses
              </Text>
              <HStack gap={3} wrap="wrap">
                <Badge variant="success" dot>
                  Active
                </Badge>
                <Badge variant="warning" dot>
                  Pending Renewal
                </Badge>
                <Badge variant="error" dot>
                  Lapsed
                </Badge>
                <Badge variant="default" dot>
                  Cancelled
                </Badge>
                <Badge variant="info" dot>
                  Quote
                </Badge>
              </HStack>
            </VStack>

            {/* Claim Statuses */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                Claim Statuses
              </Text>
              <HStack gap={3} wrap="wrap">
                <HStack gap={1}>
                  <Icon icon={Clock} size="xs" color="warning" />
                  <Badge variant="warning">Under Review</Badge>
                </HStack>
                <HStack gap={1}>
                  <Icon icon={Check} size="xs" color="success" />
                  <Badge variant="success">Approved</Badge>
                </HStack>
                <HStack gap={1}>
                  <Icon icon={X} size="xs" color="error" />
                  <Badge variant="error">Denied</Badge>
                </HStack>
                <HStack gap={1}>
                  <Icon icon={DollarSign} size="xs" color="primary" />
                  <Badge variant="primary">Paid</Badge>
                </HStack>
              </HStack>
            </VStack>

            {/* Payment Statuses */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                Payment Statuses
              </Text>
              <HStack gap={3} wrap="wrap">
                <Badge variant="success" pill>
                  Paid
                </Badge>
                <Badge variant="warning" pill>
                  Due Soon
                </Badge>
                <Badge variant="error" pill>
                  Overdue
                </Badge>
                <Badge variant="info" pill>
                  Scheduled
                </Badge>
              </HStack>
            </VStack>

            {/* Risk Levels */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                Risk Assessment
              </Text>
              <HStack gap={3} wrap="wrap">
                <Badge variant="success" size="sm">
                  Low Risk
                </Badge>
                <Badge variant="warning" size="sm">
                  Medium Risk
                </Badge>
                <Badge variant="error" size="sm">
                  High Risk
                </Badge>
              </HStack>
            </VStack>

            {/* Document Types */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                Document Labels
              </Text>
              <HStack gap={3} wrap="wrap">
                <HStack gap={1}>
                  <Icon icon={Info} size="xs" color="secondary" />
                  <Badge variant="default" size="sm">
                    Declaration Page
                  </Badge>
                </HStack>
                <HStack gap={1}>
                  <Icon icon={AlertCircle} size="xs" color="warning" />
                  <Badge variant="warning" size="sm">
                    Signature Required
                  </Badge>
                </HStack>
                <HStack gap={1}>
                  <Icon icon={Check} size="xs" color="success" />
                  <Badge variant="success" size="sm">
                    Verified
                  </Badge>
                </HStack>
              </HStack>
            </VStack>
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const BadgeShowcase: ShowcaseDefinition = {
  id: 'badge',
  name: 'Badge',
  category: 'display',
  description: 'Small status indicator for highlighting information',
  propsDefinition: [
    {
      name: 'variant',
      type: 'select',
      options: ['default', 'success', 'warning', 'error', 'info', 'primary'],
      defaultValue: 'default',
      description: 'Color variant of the badge',
    },
    {
      name: 'size',
      type: 'select',
      options: ['sm', 'md'],
      defaultValue: 'md',
      description: 'Size of the badge (sm=18px, md=22px height)',
    },
    {
      name: 'dot',
      type: 'boolean',
      defaultValue: false,
      description: 'Show a colored dot indicator',
    },
    {
      name: 'pill',
      type: 'boolean',
      defaultValue: false,
      description: 'Use pill shape with full border-radius',
    },
  ],
  accessibility: {
    ariaAttributes: [],
    keyboardNav: [],
    notes: [
      'Badges convey status visually - ensure accompanying text for screen readers',
      'Dot indicators are aria-hidden and purely decorative',
      'Color should not be the only way to convey meaning',
    ],
  },
  component: BadgeShowcaseComponent,
};
